// @givmo/app-admin — Documents → Generate report dialog (07-3b gap ③).
//
// Absorbs the Flutter webapp's Reporting screen (`report_screen.dart` + `ReportsCubit`) into the
// Documents surface: pick a year, POST `/users/me/company/donations/reports` with the year's
// Jan 1 – Dec 31 period (the exact legacy wire shape — see `buildGenerateReportBody`), and the
// backend synchronously inserts a PENDING `StatisticsReport` row + enqueues the render worker.
// The wire response is a bare 200 with a JSON-null body ("accepted"), so success here means
// REQUESTED, not rendered: the new row appears in the Documents list as Pending and the finished
// PDF is BOTH emailed to the company admin AND downloadable from the list once Completed (the
// worker `send_annual_report_company` + `mark_email_sent` path).
//
// Wiring: the generated `donationsGenerateCompanyStatsReportMutation`; on success invalidate the
// list query (the fresh Pending row shows up immediately), toast, close — the add-card-dialog
// pattern. Re-requesting the same year while a row is active is safe: the backend double-tap guard
// dedupes identical requests within 24h (still a 200), so the invalidated list simply re-shows the
// existing row. Errors surface inline (message + support reference) and keep the dialog open.
//
// Year bounds mirror the Flutter dropdown: 2023 (`REPORTS_FIRST_YEAR`) through the current year,
// defaulting to the current year.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  donationsGenerateCompanyStatsReportMutation,
  donationsListCompanyStatsReportsQueryKey,
} from '@givmo/api-client/query';
import { isGivmoError, supportReference } from '@givmo/api-client';
import { Button, Dialog, Select, testIdProp, toast, type SelectOption } from '@givmo/design-system';
import { buildGenerateReportBody, reportYearOptions } from './documents-decode';

const PREFIX = 'documents-generate';

const GENERIC_ERROR = 'Could not generate the report. Please try again.';

export interface GenerateReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GenerateReportDialog({ open, onOpenChange }: GenerateReportDialogProps) {
  const queryClient = useQueryClient();

  const currentYear = String(new Date().getFullYear());
  const yearOptions: SelectOption[] = reportYearOptions(new Date().getFullYear()).map((value) => ({
    value,
  }));

  const [year, setYear] = useState<string>(currentYear);
  const [error, setError] = useState<string | null>(null);
  const [errorReference, setErrorReference] = useState<string | null>(null);

  // Re-seed the year to the current one and clear any prior error whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setYear(currentYear);
      setError(null);
      setErrorReference(null);
    }
  }, [open, currentYear]);

  const generateMutation = useMutation({
    ...donationsGenerateCompanyStatsReportMutation(),
    onSuccess: async () => {
      // The synchronous INSERT means the new Pending row is already listable — refetch so it shows
      // up the moment the dialog closes.
      await queryClient.invalidateQueries({
        queryKey: donationsListCompanyStatsReportsQueryKey(),
      });
      toast.success('Report requested — it will be emailed to you and appear here when ready.');
      onOpenChange(false);
    },
    onError: (err) => {
      setError(isGivmoError(err) ? err.message : GENERIC_ERROR);
      setErrorReference(isGivmoError(err) ? supportReference(err) : null);
    },
  });

  const submitting = generateMutation.isPending;

  const submit = () => {
    if (submitting) return; // single-flight: a second click while pending is a no-op.
    setError(null);
    setErrorReference(null);
    const selectedYear = Number.parseInt(year, 10);
    generateMutation.mutate({ body: buildGenerateReportBody(selectedYear) });
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <Dialog.Content
        aria-label="Generate annual report"
        className="flex flex-col gap-16"
        testId={`${PREFIX}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Generate annual report
        </Dialog.Title>
        <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          A PDF report summarizing your company’s giving for the selected year will be emailed to
          you and appear in the list once it’s ready.
        </Dialog.Description>

        <Select
          label="Report year"
          options={yearOptions}
          value={year}
          onValueChange={(value) => {
            if (value !== null) setYear(value);
            setError(null);
            setErrorReference(null);
          }}
          disabled={submitting}
          testId={`${PREFIX}-year`}
        />

        {error !== null && (
          <div className="flex flex-col gap-4">
            <p
              className="font-sans text-s14 font-w500 tracking-normal text-danger-700"
              {...testIdProp(`${PREFIX}-error`)}
            >
              {error}
            </p>
            {errorReference !== null && (
              <p
                className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary"
                {...testIdProp(`${PREFIX}-error-reference`)}
              >
                {errorReference}
              </p>
            )}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-8">
          <Dialog.Close
            render={<Button appearance="saas" tone="secondary" size="md" disabled={submitting} />}
            {...testIdProp(`${PREFIX}-cancel`)}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            disabled={submitting}
            loading={submitting}
            onClick={submit}
            testId={`${PREFIX}-confirm`}
          >
            Generate report
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
